/**
 * What the Interact button does for the mission itself: work the radio, kick the lever,
 * take the officer at gunpoint, let a captive out, put on a dead man's uniform, take a fallen
 * ranger's tags - and, for the expansion (10 §3), work a `useObjects` target (held still), search the
 * target's body, hand over the quest item, meet a waiting contact. Called from ranger/actions.ts,
 * which owns the button and the everyday interactions.
 */

import type { Ctx } from '../ctx';
import { DRAG, EXPANSION, GEAR, OBJECTIVES } from '../tuning';
import type { Enemy } from '../types';
import { dist, openCells, propDef, propDestroyed, propDistance } from './common';
import { nearInterior, setDisguise, wearsUniform } from './disguise';
import { captiveLocked, usableLocked, usableProp } from './kinds';
import { usableTarget, useActionTicks } from './kindsMulti';
import { captureOfficer, officerWouldSurrender } from './officer';
import { deliverQuestItem, deliveryInReach } from './questItem';
import { lootableBody } from './target';
import { markUsed } from './useAction';

export type ObjectiveInteract =
  /** Mission 2's radio, mission 8's lever (once its pyramid is blown). */
  | { kind: 'use'; propId: number; what: 'radio' | 'lever' }
  /** The officer is at gunpoint and no guard can save him: take him. */
  | { kind: 'capture'; enemy: Enemy }
  /** A held captive with nothing left between him and the ranger. */
  | { kind: 'free'; captiveId: number }
  /** A contact waiting at the rendezvous (the defector): meet him and he falls in behind. */
  | { kind: 'meet'; captiveId: number }
  /** A `useObjects` target in reach. `ticks` > 0: it is hold-still work (`Ranger.action = 'useObject'`). */
  | { kind: 'useObject'; propId: number; ticks: number }
  /** `lootTarget`: the man is down and in reach - take what he carried. */
  | { kind: 'lootBody'; enemy: Enemy }
  /** He has the quest item and stands at the hand-over prop. `place`: it is set down (`reachDistance`), not given. */
  | { kind: 'deliver'; propId: number; item: string; place: boolean }
  /** A dead soldier at a guardhouse, tent or hut: wear his uniform (02 §6.4). */
  | { kind: 'uniform'; enemy: Enemy }
  /** Tag Recovery (04 §2.3): the fallen ranger's field grave is within reach. `name` is whose tags they are. */
  | { kind: 'tags'; propId: number; name: string };

export function objectiveInteractOption(ctx: Pick<Ctx, 'world' | 'statics'>): ObjectiveInteract | null {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const r = w.ranger;
  if (r.status !== 'active') return null;

  const officer = officerWouldSurrender(ctx);
  if (officer) return { kind: 'capture', enemy: officer };

  const prop = usableProp(statics.def);
  const usable = prop !== null && (statics.def.objective.kind === 'useObject' || prop.kind === 'lever');
  if (prop && usable && !o.used && !propDestroyed(w, prop.id) && !usableLocked(ctx, prop)) {
    if (propDistance(prop, r.x, r.z) <= OBJECTIVES.useReach) {
      return { kind: 'use', propId: prop.id, what: prop.kind === 'lever' ? 'lever' : 'radio' };
    }
  }

  // The expansion's own business (10 §3): an object to work, a body to search, a thing to hand over.
  const object = usableTarget(ctx);
  if (object) return { kind: 'useObject', propId: object.id, ticks: useActionTicks(statics.def) };
  const body = lootableBody(ctx);
  if (body) return { kind: 'lootBody', enemy: body };
  const handOver = deliveryInReach(ctx);
  const item = statics.def.objective.questItem;
  if (handOver && item) {
    const place = statics.def.objective.kind === 'reachDistance';
    return { kind: 'deliver', propId: handOver.id, item: item.name, place };
  }

  for (const c of o.captives) {
    if (c.state === 'waiting' && dist(c.x, c.z, r.x, r.z) <= EXPANSION.meetReach) return { kind: 'meet', captiveId: c.id };
    if (c.state !== 'held' || dist(c.x, c.z, r.x, r.z) > OBJECTIVES.freeReach) continue;
    if (!captiveLocked(ctx, c.x, c.z)) return { kind: 'free', captiveId: c.id };
  }

  const tags = statics.def.dogTags;
  if (tags && !o.tagsRecovered) {
    const grave = propDef(statics.def, tags.propId);
    if (grave && dist(grave.x, grave.z, r.x, r.z) <= GEAR.tagsReach) {
      return { kind: 'tags', propId: tags.propId, name: tags.name };
    }
  }

  if (!o.disguised) {
    // The body first: there is rarely one in reach, and the building test is the dearer of the two.
    const inReach = (e: Enemy): boolean => dist(e.x, e.z, r.x, r.z) <= DRAG.reach;
    const donor = w.enemies.find((e) => e.state === 'dead' && wearsUniform(e.kind) && inReach(e));
    if (donor && nearInterior(ctx, r.x, r.z)) return { kind: 'uniform', enemy: donor };
  }
  return null;
}

export function performObjectiveInteract(ctx: Ctx, option: ObjectiveInteract): void {
  const o = ctx.world.objective;
  const r = ctx.world.ranger;
  switch (option.kind) {
    case 'useObject':
      if (option.ticks <= 0) markUsed(ctx, option.propId);
      else {
        // Timed like first aid: `movement.ts` cancels it the moment he moves, and a hit knocks it out of his hands.
        r.action = 'useObject';
        r.actionTicks = 0;
        o.usingPropId = option.propId;
      }
      break;
    case 'lootBody':
      o.targetLooted = true;
      // Nobody else will be going through his pockets now.
      option.enemy.salvaged = true;
      break;
    case 'deliver':
      deliverQuestItem(ctx);
      break;
    case 'meet':
      for (const c of o.captives) {
        if (c.id !== option.captiveId || c.state !== 'waiting') continue;
        c.state = 'following';
        o.opened = true;
        ctx.events.push({ type: 'captive', id: c.id, what: 'freed' });
      }
      break;
    case 'capture':
      captureOfficer(ctx, option.enemy);
      break;
    case 'use':
      o.used = true;
      if (option.what === 'lever') {
        // The kick opens every tiger pit at once (01 §3, mission 8).
        openCells(ctx);
        ctx.events.push({ type: 'pickup', what: 'THE PITS ARE OPEN' });
      } else ctx.events.push({ type: 'pickup', what: 'CODE BOOK TAKEN' });
      break;
    case 'free':
      openCells(ctx, (c) => c.id === option.captiveId);
      break;
    case 'uniform':
      setDisguise(ctx, true);
      break;
    case 'tags':
      o.tagsRecovered = true;
      ctx.events.push({ type: 'tagsRecovered' });
      ctx.events.push({ type: 'pickup', what: `DOG TAGS RECOVERED - ${option.name.toUpperCase()}` });
      break;
  }
}
